/**
 * DateTimePicker — locale-independent 24h date/time input.
 *
 *   • Date part is shown and typed as DD/MM/YYYY (Turkish convention)
 *   • Time part is shown and typed as HH:mm (24 hour, no AM/PM)
 *   • Value in/out is an ISO 8601 string (same contract as the former
 *     <input type="datetime-local" />) — safe to drop in anywhere the
 *     old component was being used.
 *
 * The two pieces live on top of hidden native <input type="date"> /
 * <input type="time"> elements so the browser-native picker is still
 * available for calendar / clock selection, but the visible text input
 * always renders the Turkish 24-hour format regardless of OS locale.
 */
import { useEffect, useRef, useState } from "react";
import { Calendar as CalIcon, Clock as ClockIcon } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

/** ISO ("2025-11-21T17:30:00Z") -> {date:"21/11/2025", time:"17:30"} */
export const isoToParts = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "", time: "" };
  return {
    date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

/** {date:"21/11/2025", time:"17:30"} -> ISO string (or "" if invalid) */
export const partsToIso = ({ date, time }) => {
  if (!date) return "";
  const dm = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
  if (!dm) return "";
  const tm = /^(\d{2}):(\d{2})$/.exec(time || "00:00");
  if (!tm) return "";
  const [, dd, mm, yyyy] = dm;
  const [, hh, mi] = tm;
  const d = new Date(
    Number(yyyy), Number(mm) - 1, Number(dd),
    Number(hh), Number(mi), 0, 0,
  );
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
};

/** Format an ISO as "DD/MM/YYYY HH:mm" for read-only display. */
export const formatDMY = (iso, withTime = true) => {
  const p = isoToParts(iso);
  if (!p.date) return "";
  return withTime ? `${p.date} ${p.time}` : p.date;
};

/** Auto-insert slashes while typing: "21112025" -> "21/11/2025". */
const maskDate = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  const parts = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean);
  return parts.join("/");
};
/** Auto-insert ":" while typing: "1730" -> "17:30". */
const maskTime = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2)}`;
};

/**
 * @param {object}   props
 * @param {string}   props.value         ISO string (or "" for empty)
 * @param {function} props.onChange      receives new ISO string (or "")
 * @param {string?}  props.testId
 * @param {boolean?} props.error         render in error state (red border)
 * @param {string?}  props.className
 * @param {boolean?} props.dateOnly      hide the time input (date-only picker)
 */
export function DateTimePicker({
  value, onChange, testId = "dtp", error = false,
  className = "", dateOnly = false, placeholder,
}) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const nativeDateRef = useRef(null);
  const nativeTimeRef = useRef(null);

  // Sync external ISO -> internal masked strings.
  useEffect(() => {
    const p = isoToParts(value);
    setDate(p.date);
    setTime(p.time || (dateOnly ? "" : ""));
  }, [value, dateOnly]);

  const emit = (nextDate, nextTime) => {
    const iso = partsToIso({
      date: nextDate,
      time: dateOnly ? "00:00" : (nextTime || "00:00"),
    });
    onChange(iso);
  };

  const onDateChange = (raw) => {
    const next = maskDate(raw);
    setDate(next);
    if (next.length === 10 || next.length === 0) emit(next, time);
  };
  const onTimeChange = (raw) => {
    const next = maskTime(raw);
    setTime(next);
    if (next.length === 5 || next.length === 0) emit(date, next);
  };

  const openNativeDate = () => {
    const el = nativeDateRef.current;
    if (!el) return;
    try { el.showPicker ? el.showPicker() : el.click(); } catch { el.click(); }
  };
  const openNativeTime = () => {
    const el = nativeTimeRef.current;
    if (!el) return;
    try { el.showPicker ? el.showPicker() : el.click(); } catch { el.click(); }
  };

  const borderCls = error
    ? "border-red-400 focus-within:border-red-500 ring-red-100"
    : "border-stone-300 focus-within:border-indigo-400 ring-indigo-100";
  const base = `inline-flex items-center h-9 bg-white border rounded-md px-2.5 gap-1.5 focus-within:ring-2 ${borderCls}`;

  // Native inputs - hidden visually but kept in the DOM so showPicker()
  // still works. We sync them back onChange so the native calendar
  // widget stays usable for users who prefer to pick visually.
  const isoDate = (() => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
  })();

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testId}>
      <div className={base}>
        <CalIcon
          size={13}
          className="text-stone-400 cursor-pointer"
          onClick={openNativeDate}
          data-testid={`${testId}-date-icon`}
        />
        <input
          type="text"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeholder={placeholder || "GG/AA/YYYY"}
          inputMode="numeric"
          maxLength={10}
          className="w-[108px] outline-none text-sm bg-transparent"
          data-testid={`${testId}-date`}
        />
        <input
          ref={nativeDateRef}
          type="date"
          value={isoDate}
          onChange={(e) => {
            const v = e.target.value; // "2025-11-21"
            if (!v) { onDateChange(""); return; }
            const [y, m, d] = v.split("-");
            onDateChange(`${d}${m}${y}`);
          }}
          className="w-0 h-0 opacity-0 absolute pointer-events-none"
          tabIndex={-1}
        />
      </div>
      {!dateOnly && (
        <div className={base}>
          <ClockIcon
            size={13}
            className="text-stone-400 cursor-pointer"
            onClick={openNativeTime}
            data-testid={`${testId}-time-icon`}
          />
          <input
            type="text"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="SS:DD"
            inputMode="numeric"
            maxLength={5}
            className="w-[62px] outline-none text-sm bg-transparent"
            data-testid={`${testId}-time`}
          />
          <input
            ref={nativeTimeRef}
            type="time"
            value={time.length === 5 ? time : ""}
            onChange={(e) => onTimeChange(e.target.value.replace(":", ""))}
            step="60"
            className="w-0 h-0 opacity-0 absolute pointer-events-none"
            tabIndex={-1}
          />
        </div>
      )}
    </div>
  );
}

export default DateTimePicker;
